import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { BookOpen, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';
import { Skeleton } from '../ui/Skeleton';

interface ModuleGrade {
  module_id: string;
  module_name: string;
  order_index: number;
  grade: number | null;
}

interface CourseGrades {
  course_id: string;
  course_name: string;
  modules_average: number;
  evaluation_1: number | null;
  evaluation_2: number | null;
  evaluation_3: number | null;
  final_evaluation: number | null;
  final_average: number;
  module_grades: ModuleGrade[];
}

export function Grades() {
  const { profile } = useAuth();
  const [courseGrades, setCourseGrades] = useState<CourseGrades[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCourses, setExpandedCourses] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (profile?.id) {
      loadGrades();
    }
  }, [profile?.id]);

  const loadGrades = async () => {
    try {
      const { data: enrollments } = await supabase
        .from('course_enrollments')
        .select('course_id, courses!inner(id, name, status)')
        .eq('alumno_id', profile!.id)
        .eq('status', 'active')
        .eq('courses.status', 'active');

      if (!enrollments || enrollments.length === 0) {
        setLoading(false);
        return;
      }

      const gradesData = await Promise.all(
        enrollments.map(async (enrollment: any) => {
          const courseId = enrollment.courses.id;
          const courseName = enrollment.courses.name;

          const { data: evaluation } = await supabase
            .from('course_evaluations')
            .select('*')
            .eq('course_id', courseId)
            .eq('alumno_id', profile!.id)
            .single();

          const { data: modules } = await supabase
            .from('modules')
            .select('id, name, order_index')
            .eq('course_id', courseId)
            .order('order_index', { ascending: true });

          const moduleGrades = await Promise.all(
            (modules || []).map(async (module) => {
              const { data: submission } = await supabase
                .from('module_submissions')
                .select('grade')
                .eq('module_id', module.id)
                .eq('alumno_id', profile!.id)
                .maybeSingle();

              return {
                module_id: module.id,
                module_name: module.name,
                order_index: module.order_index,
                grade: submission?.grade ? parseFloat(submission.grade) : null,
              };
            })
          );

          return {
            course_id: courseId,
            course_name: courseName,
            modules_average: evaluation?.modules_average ? parseFloat(evaluation.modules_average) : 0,
            evaluation_1: evaluation?.evaluation_1 ? parseFloat(evaluation.evaluation_1) : null,
            evaluation_2: evaluation?.evaluation_2 ? parseFloat(evaluation.evaluation_2) : null,
            evaluation_3: evaluation?.evaluation_3 ? parseFloat(evaluation.evaluation_3) : null,
            final_evaluation: evaluation?.final_evaluation ? parseFloat(evaluation.final_evaluation) : null,
            final_average: evaluation?.final_average ? parseFloat(evaluation.final_average) : 0,
            module_grades: moduleGrades,
          };
        })
      );

      setCourseGrades(gradesData);
    } catch (error) {
      console.error('Error loading grades:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCourse = (courseId: string) => {
    setExpandedCourses(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div>
          <h1 className="text-display text-3xl font-bold text-white mb-2">Mis Notas</h1>
          <p className="text-muted">Cargando calificaciones…</p>
        </div>
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="glass-surface rounded-xl border border-white/[0.06] p-6 space-y-4">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-10 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (courseGrades.length === 0) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div>
          <h1 className="text-display text-3xl font-bold text-white mb-2">Mis Notas</h1>
          <p className="text-muted">Consulta tu rendimiento académico</p>
        </div>
        <div className="glass-surface rounded-xl border border-white/[0.06]">
          <EmptyState
            icon={BookOpen}
            title="Sin cursos registrados"
            description="No estás inscrito en ningún curso activo. Cuando ingreses a uno, tus calificaciones aparecerán aquí."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div>
        <h1 className="text-display text-3xl font-bold text-white mb-2">Mis Notas</h1>
        <p className="text-muted">Consulta tu rendimiento académico</p>
      </div>

      {courseGrades.map((course) => {
        const isExpanded = expandedCourses[course.course_id];

        return (
          <div key={course.course_id} className="glass-surface rounded-xl border border-white/[0.06] overflow-hidden">
            <div className="p-6 border-b border-white/[0.06]">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">{course.course_name}</h2>
                  <p className="text-sm text-muted">Detalle de calificaciones</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-cubus-lime">{course.final_average.toFixed(1)}</div>
                  <div className="text-sm text-muted mt-1">Promedio Final</div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <div
                className="glass-deep px-6 py-4 border-b border-white/[0.06] cursor-pointer hover:bg-white/[0.04] transition-all duration-200 group"
                onClick={() => toggleCourse(course.course_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-white">Módulo</span>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-cubus-lime" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted group-hover:text-cubus-lime transition-colors" />
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-sm font-semibold flex items-center gap-2 transition-all duration-200">
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Ocultar Detalle
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Ver Detalle de Notas
                        </>
                      )}
                    </button>
                    <span className="text-sm font-semibold text-white">Nota</span>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <table className="w-full">
                  <tbody>
                    {course.module_grades.map((module) => (
                      <tr key={module.module_id} className="border-b border-white/[0.06] hover:bg-white/[0.04]">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cubus-lime/10 flex items-center justify-center">
                              <span className="text-xs font-bold text-cubus-lime">M{module.order_index}</span>
                            </div>
                            <span className="text-sm text-white">{module.module_name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-center min-w-[100px]">
                          {module.grade !== null ? (
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                              module.grade >= 6
                                ? 'bg-green-500/20 text-green-400'
                                : module.grade >= 4
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {module.grade.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-faint">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="border-b border-white/[0.06] bg-black/30 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-cubus-lime" />
                    <span className="text-sm font-semibold text-white">Promedio Módulos (10%)</span>
                  </div>
                  <span className="text-lg font-bold text-cubus-lime">
                    {course.modules_average.toFixed(1)}
                  </span>
                </div>
              </div>

              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06] glass-deep">
                    <th className="text-left p-4 text-sm font-semibold text-white/70">Evaluación</th>
                    <th className="text-center p-4 text-sm font-semibold text-white/70 min-w-[100px]">Nota</th>
                    <th className="text-center p-4 text-sm font-semibold text-white/70 min-w-[120px]">Ponderación</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/[0.06] hover:bg-white/[0.04]">
                    <td className="p-4 text-sm text-white">Evaluación 1</td>
                    <td className="p-4 text-center">
                      {course.evaluation_1 !== null ? (
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          course.evaluation_1 >= 6
                            ? 'bg-green-500/20 text-green-400'
                            : course.evaluation_1 >= 4
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {course.evaluation_1.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-faint">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center text-sm text-muted">20%</td>
                  </tr>

                  <tr className="border-b border-white/[0.06] hover:bg-white/[0.04]">
                    <td className="p-4 text-sm text-white">Evaluación 2</td>
                    <td className="p-4 text-center">
                      {course.evaluation_2 !== null ? (
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          course.evaluation_2 >= 6
                            ? 'bg-green-500/20 text-green-400'
                            : course.evaluation_2 >= 4
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {course.evaluation_2.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-faint">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center text-sm text-muted">20%</td>
                  </tr>

                  <tr className="border-b border-white/[0.06] hover:bg-white/[0.04]">
                    <td className="p-4 text-sm text-white">Evaluación 3</td>
                    <td className="p-4 text-center">
                      {course.evaluation_3 !== null ? (
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          course.evaluation_3 >= 6
                            ? 'bg-green-500/20 text-green-400'
                            : course.evaluation_3 >= 4
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {course.evaluation_3.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-faint">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center text-sm text-muted">20%</td>
                  </tr>

                  <tr className="border-b border-white/[0.06] hover:bg-white/[0.04]">
                    <td className="p-4 text-sm text-white">Evaluación Final</td>
                    <td className="p-4 text-center">
                      {course.final_evaluation !== null ? (
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          course.final_evaluation >= 6
                            ? 'bg-green-500/20 text-green-400'
                            : course.final_evaluation >= 4
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {course.final_evaluation.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-faint">-</span>
                      )}
                    </td>
                    <td className="p-4 text-center text-sm text-muted">30%</td>
                  </tr>

                  <tr className="glass-deep border-t-2 border-cubus-lime">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-cubus-lime" />
                        <span className="font-bold text-white">Promedio Final</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-2xl font-bold text-cubus-lime">
                        {course.final_average.toFixed(1)}
                      </span>
                    </td>
                    <td className="p-4 text-center text-sm text-muted">100%</td>
                  </tr>
                </tbody>
              </table>

              <div className="p-4 glass-deep border-t border-white/[0.06]">
                <div className="text-xs text-muted">
                  <p className="mb-1"><strong>Sistema de calificación:</strong></p>
                  <ul className="space-y-1 ml-4">
                    <li>• Promedio Módulos: 10% del total</li>
                    <li>• Evaluaciones 1, 2 y 3: 20% cada una</li>
                    <li>• Evaluación Final: 30%</li>
                    <li>• Nota mínima de aprobación: 4.0</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
